import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import Button from "../../components/Button";
import Dropdown from "../../components/Dropdown";
import { Input } from "../../components/Input";
import LeftSide from "../../components/LeftSide";
import PaddingContainer from "../../components/PaddingContainer";
import Phone from "../../components/PhoneInput";
import PreviewImagePicker from "../../components/preview-picker";
import { LocationIcon, MailIcon, UserIcon } from "../../constant";
import {
  getAllCountries,
  getCityFromCountry,
  getStatesFromCountry,
} from "../../lib/locations-lib";
import useApi from "../../useApi";
import classes from "./AddCorporates.module.css";
import Loader from "../../components/Loader";
const initialValues = {
  corporateName: "",
  corporateLogo: "",
  corporateType: "",
  corporateCountryCode: "",
  corporateCellNumber: "",
  corporateEmail: "",
  corporateAddress: "",
  corporateCity: "",
  corporateState: "",
  corporateCountry: "",
};

const corporateValidationSchema = yup.object().shape({
  corporateName: yup.string().required("Corporate name is required"),
  corporateLogo: yup.string().required("Corporate logo is required"),
  corporateType: yup.string().required("Corporate type is required"),
  corporateCellNumber: yup.string().required("Phone number is required"),
  corporateEmail: yup
    .string()
    .email("Invalid email format")
    .required("Email is required"),
  corporateAddress: yup.string().required("Address is required"),
  corporateCity: yup.string().required("City is required"),
  corporateCountry: yup.string().required("Country is required"),
});

const AddCorporate = () => {
  const api = useApi();
  const location = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    if (id) {
      setIsEdit(true);

      if (location?.state) {
        populateFormWithData(location.state);
      } else {
        setLoading(true);
        getCorporateById({
          id: id,
          search: "",
          pageNumber: 1,
          pageSize: 1,
          showInactive: true,
          showUpcoming: true,
        });
      }
    }
  }, [id]);

  const getCorporateById = async (params) => {
    try {
      const res = await api.getAllCorporates(params);
      if (
        res.data?.headers?.error === 200 &&
        res.data?.body?.items?.length > 0
      ) {
        const corporate = res.data.body.items[0];
        populateFormWithData(corporate);
      } else {
        toast.error("Corporate not found");
        navigate("/corporates");
      }
    } catch (error) {
      console.error("Error fetching corporate:", error);
      toast.error("Failed to load corporate data");
    } finally {
      setLoading(false);
    }
  };

  const populateFormWithData = (data) => {
    const countryObj = getAllCountries().find(
      (c) => c.label.toLowerCase() === data.corporateCountry?.toLowerCase()
    );
    const countryValue = countryObj?.value || "";

    setFieldValue("corporateName", data.corporateName || "");
    setFieldValue("corporateLogo", data.corporateLogo || "");
    setFieldValue("corporateType", data.corporateType || "");
    setFieldValue("corporateEmail", data.corporateEmail || "");
    setFieldValue("corporateAddress", data.corporateAddress || "");
    setFieldValue("corporateCountry", countryValue);

    setTimeout(() => {
      const cities = getCityFromCountry(countryValue) || [];
      const cityObj = cities.find(
        (c) => c.label.toLowerCase() === data.corporateCity?.toLowerCase()
      );

      if (cityObj) {
        setFieldValue("corporateCity", cityObj.value);
      }

      if (data.corporateCellNumber) {
        const fullNumber = data.corporateCountryCode
          ? data.corporateCountryCode + data.corporateCellNumber
          : data.corporateCellNumber;
        setFieldValue("corporateCellNumber", fullNumber.replace(/^\+/, ""));
      }
    }, 0);
  };

  const formik = useFormik({
    initialValues,
    validationSchema: corporateValidationSchema,
    onSubmit: (values) => {
      const relatedState = getCityFromCountry(values.corporateCountry)?.find(
        (data) => data.value === values?.corporateCity
      )?.stateId;

      const params = {
        ...values,
        corporateCountry: getAllCountries()?.find(
          (data) => data?.value === values?.corporateCountry
        )?.label,
        corporateCity: getCityFromCountry(values.corporateCountry)?.find(
          (data) => data?.value === values?.corporateCity
        )?.label,
        corporateCountryCode: `+${values.corporateCellNumber?.slice(0, 2)}`,
        corporateState: getStatesFromCountry(values.corporateCountry)?.find(
          (data) => data?.value === relatedState
        )?.label,
      };

      if (id) {
        params.corporateId = +id;
        updateCorporate(params);
      } else {
        addCorporate(params);
      }
    },
  });

  const {
    touched,
    errors,
    values,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    validateForm,
    resetForm,
  } = formik;

  const handleChangeWithTouch = (field) => (value) => {
    // First set the field value
    setFieldValue(field, value);
    
    // Then mark as touched after a small delay
    setTimeout(() => {
      setFieldTouched(field, true);
    }, 50);
  };
  const addCorporate = async (params) => {
    setLoading(true);
    const res = await api.addCorporate(params);
    if (res.data?.headers?.error === 200) {
      toast.success("Corporate Added successfully");
      resetForm();
    } else {
      console.log("res error", res);
      toast.error(res.data?.headers?.message);
    }
    setLoading(false);
  };

  const updateCorporate = async (params) => {
    setLoading(true);
    const res = await api.updateCorporate(params);
    if (res.data?.headers?.error === 200) {
      toast.success("Corporate Updated successfully");
      resetForm();
    }
    setLoading(false);
  };
  const controlList = [
    {
      label: "Upload Logo",
      name: "corporateLogo",
      type: "file",
      errorText: errors.corporateLogo && touched.corporateLogo,
    },
    {
      icon: <UserIcon className={classes.icon} disabled={id} />,
      placeholder: "Enter Corporate Name",
      label: "Corporate Name",
      name: "corporateName",
      type: "text",
      disabled: id,
      errorText: errors.corporateName && touched.corporateName,
    },

    {
      icon: <UserIcon className={classes.icon} />,
      placeholder: "Select Corporate Type",
      label: "Corporate Type",
      name: "corporateType",

      errorText: errors.corporateType && touched.corporateType,
    },
    {
      placeholder: "Enter Phone Number",
      label: "Phone Number",
      name: "corporateCellNumber",
      type: "phone",
      errorText: errors.corporateCellNumber && touched.corporateCellNumber,
    },
    {
      icon: <MailIcon className={classes.icon} />,
      placeholder: "Enter Email Address",
      label: "Email",
      name: "corporateEmail",
      type: "email",
      errorText: errors.corporateEmail && touched.corporateEmail,
    },
    {
      icon: <LocationIcon className={classes.icon} />,
      placeholder: "Select Country",
      label: "Country",
      name: "corporateCountry",
      type: "dropdown",
      errorText: errors.corporateCountry && touched.corporateCountry,
      options: getAllCountries(),
    },
    {
      icon: <LocationIcon className={classes.icon} />,
      placeholder: "Select City",
      label: "City",
      name: "corporateCity",
      type: "dropdown",
      errorText: errors.corporateCity && touched.corporateCity,
      options: getCityFromCountry(values.corporateCountry),
    },
    {
      icon: <LocationIcon className={classes.icon} />,
      placeholder: "Enter Address",
      label: "Address",
      name: "corporateAddress",
      type: "text",
      errorText: errors.corporateAddress && touched.corporateAddress,
    },
  ];

  console.log("errors == >.", errors);

  console.log("Locations", location);
  return (
    <PaddingContainer
      heading={`${isEdit ? "Edit" : "Add"} Corporate`}
      style={{ height: "calc(100% - 105px)" }}
    >
      <form className={classes.formContainer} onSubmit={handleSubmit}>
        <h3 className={classes.formTitle}>Fill Out the Corporate Form</h3>
        <Row className={classes.formRow}>
          {loading ? (
            <Loader />
          ) : (
            controlList.map((control) => {
              if (control.type === "dropdown") {
                console.log("control", control);
                return (
                  <Col md={6} key={control.name}>
                    <Dropdown
                      {...control}
                      isOptional={true}
                      options={control?.options}
                      value={values[control?.name]}
                      onChange={handleChangeWithTouch(control.name)}
                      errorText={
                        touched[control?.name] && errors[control?.name]
                      }
                    />
                  </Col>
                );
              } else if (control.type === "file") {
                return (
                  <Col xs={12} key={control.name}>
                    <PreviewImagePicker
                      {...control}
                      value={values[control?.name]}
                      onChange={(e) => {
                        setFieldValue(control.name, e);
                      }}
                      errorText={
                        touched[control?.name] && errors[control?.name]
                      }
                      className={classes.previewImagePicker}
                    />
                  </Col>
                );
              } else if (control.type === "phone") {
                return (
                  <Col md={6} key={control.name}>
                    <Phone
                      labelStyle={{ fontSize: "16px" }}
                      {...control}
                      onChange={handleChangeWithTouch(control.name)}
                      value={values[control?.name]}
                      errorText={
                        touched[control?.name] && errors[control?.name]
                      }
                    />
                  </Col>
                );
              }
              return (
                <Col
                  md={control?.name === "corporateAddress" ? 12 : 6}
                  key={control.name}
                >
                  <Input
                    {...control}
                    isOptional={true}
                    leftIcon={<LeftSide disabled={control?.disabled}   icon={control?.icon} />}
                    customStyle={{ gap: "14px" }}
                    inputStyle={{ paddingLeft: "0px" }}
                    labelStyle={{ fontSize: "16px" }}
                    onChange={handleChangeWithTouch(control.name)}
                    value={values[control?.name]}
                    errorText={touched[control?.name] && errors[control?.name]}
                  />
                </Col>
              );
            })
          )}
          <Col xs={12}>
            <div className={classes.ctaWrapper}>
              <Button
                variant="secondary"
                label="Discard Changes"
                onClick={resetForm}
                disabled={loading}
              />
              <Button
                label={id ? "Update" : "Save"}
                type="submit"
                disabled={loading}
              />
            </div>
          </Col>
        </Row>
      </form>
    </PaddingContainer>
  );
};

export default AddCorporate;
